"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toStagedPlacementOptionRules = toStagedPlacementOptionRules;
exports.applyPlacementOptionVisibility = applyPlacementOptionVisibility;
exports.computePlacementVariantVisibility = computePlacementVariantVisibility;
const variantUtils_1 = require("./variantUtils");
function toStagedPlacementOptionRules(rules) {
    return rules.map((rule) => {
        var _a;
        return ({
            optionName: rule.optionName,
            display: rule.display,
            sortOrder: (_a = rule.sortOrder) !== null && _a !== void 0 ? _a : null,
            valueRules: rule.valueRules.map((valueRule) => {
                var _a;
                return ({
                    optionValue: valueRule.optionValue,
                    display: valueRule.display,
                    sortOrder: (_a = valueRule.sortOrder) !== null && _a !== void 0 ? _a : null,
                });
            }),
        });
    });
}
/**
 * Admin-entered option/value strings may not match Shopify casing (e.g. SIZE vs Size, xs vs XS).
 * All matching uses this normalized form.
 */
function norm(s) {
    return s.trim().toLowerCase();
}
function buildPlacementLookup(rules) {
    const ruleByNormOption = new Map();
    const valueDisplayByNormOption = new Map();
    for (const r of rules) {
        const optKey = norm(r.optionName);
        ruleByNormOption.set(optKey, r);
        if (r.valueRules.length === 0)
            continue;
        let valueMap = valueDisplayByNormOption.get(optKey);
        if (!valueMap) {
            valueMap = new Map();
            valueDisplayByNormOption.set(optKey, valueMap);
        }
        for (const vr of r.valueRules) {
            valueMap.set(norm(vr.optionValue), vr.display);
        }
    }
    return { ruleByNormOption, valueDisplayByNormOption };
}
/**
 * Strips hidden options/values from API payloads only (no Shopify / DB mutation).
 * Variant `option1`–`3` align with product options by **position**;
 * we use the **pre-filter** option order so slots stay aligned when an option row is dropped.
 */
function applyPlacementOptionVisibility(options, variants, rules) {
    if (rules == null || rules.length === 0) {
        return { optionInfo: options, variantInfo: variants };
    }
    const { ruleByNormOption, valueDisplayByNormOption } = buildPlacementLookup(rules);
    const optionsSorted = [...options].sort((a, b) => a.position - b.position);
    const nextOptions = [];
    for (const opt of optionsSorted) {
        const rule = ruleByNormOption.get(norm(opt.name));
        if (rule && !rule.display) {
            continue;
        }
        let values = opt.values.map((v) => (Object.assign({}, v)));
        const valueMap = valueDisplayByNormOption.get(norm(opt.name));
        if (valueMap && valueMap.size > 0) {
            values = values.filter((v) => valueMap.get(norm(v.value)) !== false);
        }
        nextOptions.push(Object.assign(Object.assign({}, opt), { values }));
    }
    const nameAtIndex = (index) => optionsSorted[index] ? optionsSorted[index].name : null;
    const isValueAllowed = (optionName, value) => {
        if (optionName == null || value == null) {
            return true;
        }
        const rule = ruleByNormOption.get(norm(optionName));
        if (!rule) {
            return true;
        }
        if (!rule.display) {
            return false;
        }
        const vm = valueDisplayByNormOption.get(norm(optionName));
        if (!vm || vm.size === 0) {
            return true;
        }
        const explicit = vm.get(norm(value));
        return explicit === undefined ? true : explicit;
    };
    const valueForSlot = (optionName, val) => {
        if (optionName == null) {
            return val;
        }
        if (val == null) {
            return null;
        }
        return isValueAllowed(optionName, val) ? val : null;
    };
    const nextVariants = variants.map((v) => (Object.assign(Object.assign({}, v), { option1: valueForSlot(nameAtIndex(0), v.option1), option2: valueForSlot(nameAtIndex(1), v.option2), option3: valueForSlot(nameAtIndex(2), v.option3) })));
    return { optionInfo: nextOptions, variantInfo: nextVariants };
}
/**
 * Badge counts for admin placement visibility. Present only when rules hide
 * at least one otherwise-displayed variant (same rule as the link transformer).
 */
function computePlacementVariantVisibility(originalOptions, originalVariants, appliedOptions, appliedVariants) {
    const totalCount = (0, variantUtils_1.displayedVariantShopifyIds)(originalVariants, originalOptions).size;
    const visibleCount = (0, variantUtils_1.displayedVariantShopifyIds)(appliedVariants, appliedOptions).size;
    if (visibleCount < totalCount) {
        return { visibleCount, totalCount };
    }
    return undefined;
}
